import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  { value: 'Returnable', label: 'Returnable' },
  { value: 'Non-Returnable', label: 'Non-Returnable' }
];

const CATEGORY_OPTIONS = [
  { value: 'Fabric', label: 'Fabric' },
  { value: 'Trims & Accessories', label: 'Trims & Accessories' },
  { value: 'Article', label: 'Article' },
  { value: 'Wastage', label: 'Wastage' },
  { value: 'Asset', label: 'Asset' }
];

const ASSET_OPTIONS = [
  { value: 'Laptop', label: 'Laptop' },
  { value: 'Mouse', label: 'Mouse' },
  { value: 'Keyboard', label: 'Keyboard' },
  { value: 'Other', label: 'Other' }
];

export default function GatePass({ user }) {
  const navigate = useNavigate();
  const { canView, canCreate, canEdit, canDelete, canApprove } = usePermissions(user);
  
  // Check if user has permission to view gate pass
  if (!canView('gate_pass')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view Gate Pass.</p>
          <Button onClick={() => navigate('/design-studio')}>Back to Design Studio</Button>
        </div>
      </div>
    );
  }
  
  const [gatePassList, setGatePassList] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedGatePass, setSelectedGatePass] = useState(null);
  const [deleteAuthOpen, setDeleteAuthOpen] = useState(false);
  const [approveAuthOpen, setApproveAuthOpen] = useState(false);
  const [inwardAuthOpen, setInwardAuthOpen] = useState(false);
  const [gpToDelete, setGpToDelete] = useState(null);
  const [gpToApprove, setGpToApprove] = useState(null);
  const [gpForInward, setGpForInward] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [approvalCode, setApprovalCode] = useState('');
  const [inwardCode, setInwardCode] = useState('');

  // Form fields - now supporting multiple items
  const [formData, setFormData] = useState({
    type: '',
    purpose: ''
  });

  // Multiple items array
  const [gatePassItems, setGatePassItems] = useState([]);

  // Current item being added
  const [currentItem, setCurrentItem] = useState({
    category: '',
    item_code: '',
    color_code: '',
    quantity: '',
    custom_asset: ''  // For "Other" asset type
  });

  const [items, setItems] = useState([]);
  const [colors, setColors] = useState([]);

  useEffect(() => {
    fetchGatePasses();
  }, []);

  const fetchGatePasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gate-passes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGatePassList(response.data);
    } catch (error) {
      console.error('Failed to fetch gate passes:', error);
      toast.error('Failed to fetch gate passes');
    }
  };

  // Auto-refresh every 5 seconds, but pause when dialog is open
  useAutoRefresh(fetchGatePasses, 5, formOpen || viewOpen || deleteAuthOpen || approveAuthOpen || inwardAuthOpen);

  const handleCategoryChange = async (category) => {
    setCurrentItem({ ...currentItem, category, item_code: '', color_code: '', custom_asset: '' });
    setColors([]);
    setItems([]);
    
    // Asset category doesn't need API call
    if (category === 'Asset') {
      setItems(ASSET_OPTIONS);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let codeField = '';
      let labelField = '';

      if (category === 'Fabric') {
        endpoint = '/fabrics';
        codeField = 'ln_code';
        labelField = 'composition';
      } else if (category === 'Trims & Accessories') {
        endpoint = '/trims';
        codeField = 'ln_z_code';
        labelField = 'item_name';
      } else if (category === 'Article') {
        endpoint = '/articles-master';
        codeField = 'ln_x_code';
        labelField = 'ln_x_code';
      } else if (category === 'Wastage') {
        endpoint = '/wastage';
        codeField = 'id';
        labelField = 'fabric_code';
      }

      if (endpoint) {
        const response = await axios.get(`${API}${endpoint}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (category === 'Wastage') {
          // Special handling for wastage items
          setItems(response.data.map(item => ({
            value: item.id,
            label: `${item.fabric_code} - ${item.fabric_name} (${item.color_code}) - ${item.remaining_quantity_meters}m`,
            colors: [],
            wastageData: item
          })));
        } else {
          setItems(response.data.map(item => ({
            value: item[codeField],
            label: `${item[codeField]}${item[labelField] ? ' - ' + item[labelField] : ''}`,
            colors: item.colors || []
          })));
        }
      }
    } catch (error) {
      console.error('Failed to fetch items:', error);
      toast.error('Failed to load items');
    }
  };

  const handleItemChange = (itemCode) => {
    setCurrentItem({ ...currentItem, item_code: itemCode, color_code: '' });
    
    const selectedItem = items.find(item => item.value === itemCode);
    if (selectedItem && selectedItem.colors && selectedItem.colors.length > 0) {
      setColors(selectedItem.colors.map(c => ({
        value: c.color_code,
        label: `${c.color_code} - ${c.color_name}`
      })));
    } else {
      setColors([]);
    }
  };

  const handleAddItem = () => {
    // Validation
    if (!currentItem.category || !currentItem.item_code || !currentItem.quantity) {
      toast.error('Please fill in category, item, and quantity');
      return;
    }

    const quantity = parseFloat(currentItem.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    // For "Other" asset, need custom text
    if (currentItem.category === 'Asset' && currentItem.item_code === 'Other' && !currentItem.custom_asset) {
      toast.error('Please specify the asset name for "Other"');
      return;
    }

    // Find item details for display
    const selectedItem = items.find(item => item.value === currentItem.item_code);
    const itemLabel = selectedItem ? selectedItem.label : currentItem.item_code;

    // Build item object
    const newItem = {
      category: currentItem.category,
      item_code: currentItem.item_code,
      item_name: currentItem.category === 'Asset' && currentItem.item_code === 'Other' ? currentItem.custom_asset : itemLabel,
      color_code: currentItem.color_code || null,
      quantity: quantity,
      custom_asset: currentItem.custom_asset || null,
      // For display
      display_label: currentItem.category === 'Asset' && currentItem.item_code === 'Other' 
        ? currentItem.custom_asset 
        : itemLabel
    };

    // Add to items array
    setGatePassItems([...gatePassItems, newItem]);

    // Reset current item
    setCurrentItem({
      category: '',
      item_code: '',
      color_code: '',
      quantity: '',
      custom_asset: ''
    });
    setItems([]);
    setColors([]);
    toast.success('Item added to gate pass');
  };

  const handleRemoveItem = (index) => {
    const updatedItems = gatePassItems.filter((_, i) => i !== index);
    setGatePassItems(updatedItems);
    toast.success('Item removed');
  };

  const handleCreateGatePass = async () => {
    if (!formData.type) {
      toast.error('Please select gate pass type (Returnable/Non-Returnable)');
      return;
    }

    if (gatePassItems.length === 0) {
      toast.error('Please add at least one item to the gate pass');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/gate-passes`,
        {
          type: formData.type,
          items: gatePassItems.map(item => ({
            category: item.category,
            item_code: item.item_code,
            color_code: item.color_code,
            quantity: item.quantity,
            custom_asset: item.custom_asset
          })),
          purpose: formData.purpose || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Gate pass created successfully');
      setFormOpen(false);
      setFormData({
        type: '',
        purpose: ''
      });
      setGatePassItems([]);
      setCurrentItem({
        category: '',
        item_code: '',
        color_code: '',
        quantity: '',
        custom_asset: ''
      });
      setItems([]);
      setColors([]);
      fetchGatePasses();
    } catch (error) {
      console.error('Error creating gate pass:', error);
      toast.error(error.response?.data?.detail || 'Failed to create gate pass');
    }
  };

  const handleViewClick = async (gatePass) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gate-passes/${gatePass.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedGatePass(response.data);
      setViewOpen(true);
    } catch (error) {
      console.error('Error fetching gate pass details:', error);
      toast.error('Failed to load gate pass details');
    }
  };

  const handleApproveClick = (gatePass) => {
    setGpToApprove(gatePass);
    setApproveAuthOpen(true);
  };

  const confirmApprove = async () => {
    if (!gpToApprove) return;

    if (!approvalCode) {
      toast.error('Please enter approval code');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/gate-passes/${gpToApprove.id}/status`,
        {
          status: 'APPROVED',
          approval_code: approvalCode
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Gate pass approved successfully');
      setApproveAuthOpen(false);
      setGpToApprove(null);
      setApprovalCode('');
      fetchGatePasses();
    } catch (error) {
      console.error('Error approving gate pass:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve gate pass');
      setApproveAuthOpen(false);
      setApprovalCode('');
    }
  };

  const handleInwardClick = (gatePass) => {
    setGpForInward(gatePass);
    setInwardAuthOpen(true);
  };

  const confirmInward = async () => {
    if (!gpForInward) return;

    if (!inwardCode) {
      toast.error('Please enter inward code');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/gate-passes/${gpForInward.id}/inward`,
        {
          inward_code: inwardCode
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Inward registered successfully');
      setInwardAuthOpen(false);
      setGpForInward(null);
      setInwardCode('');
      setViewOpen(false);
      fetchGatePasses();
    } catch (error) {
      console.error('Error registering inward:', error);
      toast.error(error.response?.data?.detail || 'Failed to register inward');
      setInwardAuthOpen(false);
      setInwardCode('');
    }
  };

  const handleDeleteClick = (gatePass) => {
    setGpToDelete(gatePass);
    setDeleteAuthOpen(true);
  };

  const confirmDelete = async () => {
    if (!gpToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/gate-passes/${gpToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Gate pass deleted successfully');
      setDeleteAuthOpen(false);
      setGpToDelete(null);
      fetchGatePasses();
    } catch (error) {
      console.error('Error deleting gate pass:', error);
      toast.error('Failed to delete gate pass');
      setDeleteAuthOpen(false);
    }
  };

  const getFilteredGatePasses = () => {
    let filtered = gatePassList;
    
    // Filter by search term only
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(gp =>
        gp.gp_number.toLowerCase().includes(search) ||
        gp.item_code.toLowerCase().includes(search) ||
        (gp.item_name && gp.item_name.toLowerCase().includes(search)) ||
        gp.category.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  };

  const getStatusBadge = (gatePass) => {
    if (gatePass.status === 'PENDING') {
      return <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">PENDING</span>;
    }
    if (gatePass.status === 'REJECTED') {
      return <span className="px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800">REJECTED</span>;
    }
    if (gatePass.is_returned) {
      return <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">RETURNED</span>;
    }
    if (gatePass.type === 'Returnable') {
      return <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">RETURNABLE</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">NON-RETURNABLE</span>;
  };

  const filteredGatePasses = getFilteredGatePasses();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/design-studio')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Gate Pass
              </h1>
              <p className="text-sm text-gray-600">Manage returnable and non-returnable gate passes</p>
            </div>
          </div>
          {canCreate('gate_pass') && (
            <Button onClick={() => setFormOpen(true)} className="gap-2 bg-black hover:bg-gray-800">
              <Plus className="w-4 h-4" />
              New Gate Pass
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Tabs */}
        {/* Search Bar */}
        <div className="mb-6">
          <Input
            type="text"
            placeholder="Search by GP number, item code, item name, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        {filteredGatePasses.length === 0 ? (
          <div className="bg-white border-2 border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-500">No gate passes found</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">GP Number</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Item</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">User</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredGatePasses.map(gatePass => (
                  <tr key={gatePass.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleViewClick(gatePass)}
                        className="text-sm font-medium text-black hover:text-blue-600 hover:underline cursor-pointer transition-colors"
                      >
                        {gatePass.gp_number}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {gatePass.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {gatePass.items && gatePass.items.length > 0 
                          ? gatePass.items.map(item => item.category).join(', ')
                          : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {gatePass.taken_by || gatePass.created_by || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {gatePass.created_at ? new Date(gatePass.created_at).toLocaleDateString() : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(gatePass)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {gatePass.status === 'PENDING' && canApprove('gate_pass') && (
                          <Button
                            onClick={() => handleApproveClick(gatePass)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white shadow-sm"
                          >
                            Approve
                          </Button>
                        )}
                        {canDelete('gate_pass') && (
                          <Button
                            onClick={() => handleDeleteClick(gatePass)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Create Gate Pass Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight">New Gate Pass</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Gate Pass Type and Purpose */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Type *</Label>
                <SearchableSelect
                  options={TYPE_OPTIONS}
                  value={formData.type}
                  onChange={(value) => setFormData({ ...formData, type: value })}
                  placeholder="Select type..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Purpose (Optional)</Label>
                <Input
                  type="text"
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="Enter purpose"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Items List */}
            {gatePassItems.length > 0 && (
              <div className="border rounded p-4 space-y-2 bg-gray-50">
                <Label className="text-sm font-bold">Items Added ({gatePassItems.length})</Label>
                {gatePassItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{item.category}</div>
                      <div className="text-xs text-gray-600">{item.display_label}</div>
                      {item.color_code && <div className="text-xs text-gray-500">Color: {item.color_code}</div>}
                      <div className="text-xs font-medium mt-1">Qty: {item.quantity}</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Item Section */}
            <div className="border-t pt-4">
              <Label className="text-sm font-bold mb-3 block">Add Item</Label>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Category *</Label>
                  <SearchableSelect
                    options={CATEGORY_OPTIONS}
                    value={currentItem.category}
                    onChange={handleCategoryChange}
                    placeholder="Select category..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Item *</Label>
                  <SearchableSelect
                    options={items}
                    value={currentItem.item_code}
                    onChange={handleItemChange}
                    placeholder="Select item..."
                    className="mt-1"
                    disabled={!currentItem.category}
                  />
                </div>

                {currentItem.category === 'Asset' && currentItem.item_code === 'Other' && (
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Asset Name *</Label>
                    <Input
                      type="text"
                      value={currentItem.custom_asset}
                      onChange={(e) => setCurrentItem({ ...currentItem, custom_asset: e.target.value })}
                      placeholder="Enter asset name"
                      className="mt-1"
                    />
                  </div>
                )}

                {colors.length > 0 && currentItem.category !== 'Wastage' && currentItem.category !== 'Asset' && (
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Color</Label>
                    <SearchableSelect
                      options={colors}
                      value={currentItem.color_code}
                      onChange={(value) => setCurrentItem({ ...currentItem, color_code: value })}
                      placeholder="Select color..."
                      className="mt-1"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Quantity *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                    placeholder="Enter quantity"
                    className="mt-1"
                  />
                </div>

                <Button
                  onClick={handleAddItem}
                  variant="outline"
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Item
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={() => setFormOpen(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleCreateGatePass} className="flex-1 bg-black hover:bg-gray-800">
                Create Gate Pass
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Gate Pass Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight">Gate Pass Details</DialogTitle>
          </DialogHeader>
          {selectedGatePass && (
            <div className="space-y-6">
              {/* Gate Pass Summary */}
              <div className="p-4 border-2 border-black rounded-lg bg-gray-50">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">GP Number</p>
                    <p className="text-lg font-bold">{selectedGatePass.gp_number}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">Type</p>
                    <p className="text-lg font-bold">{selectedGatePass.is_returned ? 'RETURNED' : selectedGatePass.type}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">Status</p>
                    {getStatusBadge(selectedGatePass)}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">Total Items</p>
                    <p className="text-lg font-bold">{selectedGatePass.items?.length || 0}</p>
                  </div>
                  {selectedGatePass.purpose && (
                    <div className="col-span-2">
                      <p className="text-xs font-bold text-gray-500 uppercase">Purpose</p>
                      <p className="text-sm">{selectedGatePass.purpose}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Items List */}
              <div>
                <h3 className="text-lg font-bold uppercase tracking-tight mb-3">Items</h3>
                <div className="space-y-2">
                  {selectedGatePass.items && selectedGatePass.items.map((item, index) => (
                    <div key={index} className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase">Category</p>
                          <p className="font-bold">{item.category}</p>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase">Quantity</p>
                          <p className="font-bold">{item.quantity}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs font-bold text-gray-500 uppercase">Item</p>
                          <p className="font-medium">{item.item_code} - {item.item_name}</p>
                          {item.color_code && (
                            <p className="text-sm text-gray-600">{item.color_code} {item.color_name && `- ${item.color_name}`}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Outward Section */}
              {selectedGatePass.status === 'APPROVED' && (
                <div>
                  <h3 className="text-lg font-bold uppercase tracking-tight mb-3">Outward Details</h3>
                  <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-bold text-gray-700 uppercase">Outward Date</p>
                        <p className="font-medium">
                          {selectedGatePass.outward_date ? new Date(selectedGatePass.outward_date).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-700 uppercase">Taken By</p>
                        <p className="font-medium">{selectedGatePass.taken_by || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Inward Section (for Returnable only) */}
              {selectedGatePass.type === 'Returnable' && selectedGatePass.status === 'APPROVED' && (
                <div>
                  <h3 className="text-lg font-bold uppercase tracking-tight mb-3">Inward Details</h3>
                  {selectedGatePass.is_returned ? (
                    <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-bold text-gray-700 uppercase">Inward Date</p>
                          <p className="font-medium">
                            {selectedGatePass.inward_date ? new Date(selectedGatePass.inward_date).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-gray-700 uppercase">Returned By</p>
                          <p className="font-medium">{selectedGatePass.returned_by || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-gray-300 rounded-lg p-4 text-center">
                      <p className="text-gray-500 mb-4">Item not yet returned</p>
                      <Button
                        onClick={() => handleInwardClick(selectedGatePass)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Register Inward
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={() => setViewOpen(false)} className="bg-black hover:bg-gray-800">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Authorization Dialogs */}
      <Dialog open={approveAuthOpen} onOpenChange={setApproveAuthOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve Gate Pass</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {gpToApprove ? `Enter approval code to approve gate pass ${gpToApprove.gp_number}` : 'Enter approval code to proceed'}
            </p>
            <div>
              <Label>Approval Code</Label>
              <Input
                type="password"
                value={approvalCode}
                onChange={(e) => setApprovalCode(e.target.value)}
                placeholder="Enter approval code"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => { setApproveAuthOpen(false); setApprovalCode(''); }} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={confirmApprove} className="flex-1 bg-black hover:bg-gray-800">
                Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={inwardAuthOpen} onOpenChange={setInwardAuthOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Register Inward</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {gpForInward ? `Enter inward code to register return for gate pass ${gpForInward.gp_number}` : 'Enter inward code to proceed'}
            </p>
            <div>
              <Label>Inward Code</Label>
              <Input
                type="password"
                value={inwardCode}
                onChange={(e) => setInwardCode(e.target.value)}
                placeholder="Enter inward code"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => { setInwardAuthOpen(false); setInwardCode(''); }} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={confirmInward} className="flex-1 bg-black hover:bg-gray-800">
                Register Inward
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AuthorizationDialog
        open={deleteAuthOpen}
        onOpenChange={setDeleteAuthOpen}
        onAuthorized={confirmDelete}
        title="Delete Gate Pass"
        message={gpToDelete ? `Enter authorization code to delete gate pass ${gpToDelete.gp_number}` : 'Enter authorization code to proceed'}
      />
    </div>
  );
}
